import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GeminiHeader } from "@/components/GeminiHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { User, HazardReport } from "@/types/sorin";
import { ArrowLeft, MapPin, AlertTriangle, CheckCircle, XCircle, Search, Eye, Filter, Clock, Wrench, Camera, Upload, User as UserIcon, Calendar, Tag, MessageSquare, Lock, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RiskRatingBadge } from "@/components/RiskRatingBadge";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { useSubscriptionFeatures, useUserCompanyId } from "@/hooks/useSubscriptionFeatures";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ExtendedReport extends HazardReport {
  truth_score?: number;
  evidence_score?: number;
  vision_confidence?: number;
  audio_confidence?: number;
  consistency_score?: number;
  geo_shift_match?: number;
  peer_corroboration_score?: number;
  tamper_penalty?: number;
  photo_analysis_data?: any;
  supervisor_notes?: string;
  hazard_category?: string;
  reported_worker_name?: string;
  reporter_credibility?: number;
  clarification_response?: string;
  clarification_responded_at?: string;
  verification_status?: string;
}

interface EscalatedTask {
  id: string;
  report_id: string;
  resolution_type: string;
  action_suggested: string | null;
  action_taken: string | null;
  escalated_to: string | null;
  escalated_at: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  notes: string | null;
  photo_after: string | null;
  taken_by: string | null;
  report?: ExtendedReport;
  worker_name?: string;
}

// SupervisorTabs component with subscription-based feature gating
const SupervisorTabs = ({ 
  activeTab, 
  setActiveTab, 
  filteredReportsCount, 
  escalatedTasksCount 
}: { 
  activeTab: string; 
  setActiveTab: (tab: string) => void;
  filteredReportsCount: number;
  escalatedTasksCount: number;
}) => {
  const { companyId } = useUserCompanyId();
  const { hasFeature, isLoading } = useSubscriptionFeatures(companyId || undefined);
  
  const canUseFix = hasFeature('supervisorFix');
  
  return (
    <TabsList className="grid w-full grid-cols-2 mb-6 h-12 rounded-2xl bg-muted/50 p-1">
      <TabsTrigger 
        value="reports" 
        className="rounded-xl data-[state=active]:bg-background data-[state=active]:shadow-sm"
      >
        <Eye className="h-4 w-4 mr-2" />
        Verifikasi ({filteredReportsCount})
      </TabsTrigger>
      
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="relative w-full">
              <TabsTrigger 
                value="tasks" 
                className={`w-full rounded-xl data-[state=active]:bg-background data-[state=active]:shadow-sm ${!canUseFix ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!canUseFix}
                onClick={(e) => {
                  if (!canUseFix) {
                    e.preventDefault();
                  }
                }}
              >
                <Wrench className="h-4 w-4 mr-2" />
                Koreksi ({escalatedTasksCount})
                {!canUseFix && <Lock className="h-3 w-3 ml-2 text-muted-foreground" />}
              </TabsTrigger>
            </div>
          </TooltipTrigger>
          {!canUseFix && (
            <TooltipContent side="bottom">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span>Upgrade ke Professional untuk fitur Koreksi</span>
              </div>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </TabsList>
  );
};

const Supervisor = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [reports, setReports] = useState<ExtendedReport[]>([]);
  const [filteredReports, setFilteredReports] = useState<ExtendedReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<ExtendedReport | null>(null);
  const [supervisorNotes, setSupervisorNotes] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("pending_verification");
  const [riskFilter, setRiskFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("reports");
  const [escalatedTasks, setEscalatedTasks] = useState<EscalatedTask[]>([]);
  const [selectedTask, setSelectedTask] = useState<EscalatedTask | null>(null);
  const [completionNotes, setCompletionNotes] = useState("");
  const [afterPhotoUrl, setAfterPhotoUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [userSiteId, setUserSiteId] = useState<string | null>(null);
  const [userCompanyId, setUserCompanyId] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<Array<{ role: string; content: string }>>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const reportsPerPage = 20;

  useEffect(() => {
    checkAuthAndFetchData();
  }, []);

  // Re-fetch reports when status filter changes
  useEffect(() => {
    if (user) {
      fetchReports(userSiteId, userCompanyId);
    }
  }, [statusFilter, userSiteId, userCompanyId]);

  const checkAuthAndFetchData = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();

      if (!authUser) {
        navigate("/signup");
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from("worker_profiles")
        .select("*")
        .eq("user_id", authUser.id)
        .maybeSingle();

      if (profileError || !profile) {
        navigate("/signup");
        return;
      }

      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", authUser.id)
        .eq("role", "supervisor")
        .maybeSingle();

      const isSupervisor = profile.role === "supervisor" || roleData !== null;

      if (!isSupervisor) {
        toast({ title: "Access Denied", description: "You must be a supervisor to access this page.", variant: "destructive" });
        navigate("/home");
        return;
      }

      // Store site_id and company_id for filtering
      setUserSiteId(profile.site_id);
      setUserCompanyId(profile.company_id);

      const userData: User = {
        id: profile.id,
        displayName: profile.display_name,
        role: "Supervisor",
        team: profile.team,
        xp: 0,
        streak: profile.consecutive_safe_days,
        badges: [],
        psychometricVector: [],
        baselineRisk: profile.baseline_risk_score,
        currentRisk: profile.current_risk_score,
        anonymousMode: false,
        createdAt: new Date(profile.created_at),
      };

      setUser(userData);
      fetchReports(profile.site_id, profile.company_id);
      fetchEscalatedTasks(profile.site_id, profile.company_id);
    } catch (error) {
      console.error("Error checking auth:", error);
      navigate("/signup");
    }
  };

  const fetchReports = async (siteId?: string | null, companyId?: string | null) => {
    try {
      let query = supabase
        .from("peer_reports")
        .select(`*, reporter:worker_profiles!reporter_id(display_name, full_name, role, team, credibility_score), reported_worker:worker_profiles!reported_worker_id(display_name, full_name, role, team, current_risk_score)`)
        .order("created_at", { ascending: false });

      // Filter by site_id if available, otherwise by company_id
      const effectiveSiteId = siteId ?? userSiteId;
      const effectiveCompanyId = companyId ?? userCompanyId;
      
      if (effectiveSiteId) {
        query = query.eq("site_id", effectiveSiteId);
      } else if (effectiveCompanyId) {
        // Fallback to filtering by reporter's company - get worker IDs in company
        const { data: companyWorkers } = await supabase
          .from("worker_profiles")
          .select("id")
          .eq("company_id", effectiveCompanyId);
        
        if (companyWorkers && companyWorkers.length > 0) {
          const workerIds = companyWorkers.map(w => w.id);
          query = query.in("reporter_id", workerIds);
        }
      }

      // Filter by verification status with evidence score threshold for pending_verification
      if (statusFilter === "pending_verification") {
        // Only show pending reports with evidence_score < 20 (truly need review)
        query = query
          .eq("verification_status", "pending_verification")
          .lt("evidence_score", 20);
      } else if (statusFilter !== "all") {
        query = query.eq("verification_status", statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      const transformedReports: ExtendedReport[] = (data || []).map((report) => ({
        id: report.id,
        reporterId: report.reporter_id || "",
        location: report.location,
        description: report.description,
        hazardType: report.hazard_category as any,
        severity: report.risk_rating as any,
        status: "pending",
        timestamp: new Date(report.created_at),
        reporterName: report.reporter?.display_name || "Worker",
        xpAwarded: report.xp_awarded || 0,
        xpConfirmed: !report.xp_pending && report.verification_status === "verified",
        confidence: report.truth_score || 0,
        photoLabels: report.photo_labels as string[] | undefined,
        photoUrl: report.photo_url || undefined,
        requiresHumanReview: false,
        requiresPhoto: false,
        askClarify: false,
        truth_score: report.truth_score,
        evidence_score: report.evidence_score,
        vision_confidence: report.vision_confidence,
        audio_confidence: report.audio_confidence,
        consistency_score: report.consistency_score,
        geo_shift_match: report.geo_shift_match,
        peer_corroboration_score: report.peer_corroboration_score,
        tamper_penalty: report.tamper_penalty,
        photo_analysis_data: report.photo_analysis_data,
        supervisor_notes: report.supervisor_notes,
        hazard_category: report.hazard_category,
        reported_worker_name: report.reported_worker?.display_name || report.reported_worker?.full_name || null,
        reporter_credibility: report.reporter?.credibility_score || 100,
        clarification_response: report.clarification_response,
        clarification_responded_at: report.clarification_responded_at,
        verification_status: report.verification_status,
      }));

      setReports(transformedReports);
      setFilteredReports(transformedReports);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEscalatedTasks = async (siteId?: string | null, companyId?: string | null) => {
    try {
      const effectiveSiteId = siteId ?? userSiteId;
      const effectiveCompanyId = companyId ?? userCompanyId;

      // First get report IDs from the site/company
      let reportIds: string[] = [];
      if (effectiveSiteId) {
        const { data: siteReports } = await supabase
          .from("peer_reports")
          .select("id")
          .eq("site_id", effectiveSiteId);
        reportIds = (siteReports || []).map(r => r.id);
      } else if (effectiveCompanyId) {
        const { data: companyWorkers } = await supabase
          .from("worker_profiles")
          .select("id")
          .eq("company_id", effectiveCompanyId);
        
        if (companyWorkers && companyWorkers.length > 0) {
          const workerIds = companyWorkers.map(w => w.id);
          const { data: companyReports } = await supabase
            .from("peer_reports")
            .select("id")
            .in("reporter_id", workerIds);
          reportIds = (companyReports || []).map(r => r.id);
        }
      }

      let query = supabase
        .from("hazard_resolutions")
        .select(`
          *,
          report:peer_reports!report_id(
            id, location, description, hazard_category, risk_rating, photo_url, created_at,
            reporter:worker_profiles!reporter_id(display_name, full_name)
          ),
          worker:worker_profiles!taken_by(display_name, full_name)
        `)
        .eq("resolution_type", "escalated")
        .is("resolved_at", null)
        .order("created_at", { ascending: false });

      // Filter by report IDs if we have site/company context
      if (reportIds.length > 0) {
        query = query.in("report_id", reportIds);
      }

      const { data, error } = await query;

      if (error) throw error;

      const tasks: EscalatedTask[] = (data || []).map((task: any) => ({
        id: task.id,
        report_id: task.report_id,
        resolution_type: task.resolution_type,
        action_suggested: task.action_suggested,
        action_taken: task.action_taken,
        escalated_to: task.escalated_to,
        escalated_at: task.escalated_at,
        resolved_at: task.resolved_at,
        resolved_by: task.resolved_by,
        notes: task.notes,
        photo_after: task.photo_after,
        taken_by: task.taken_by,
        report: task.report ? {
          id: task.report.id,
          location: task.report.location,
          description: task.report.description,
          hazardType: task.report.hazard_category,
          severity: task.report.risk_rating,
          photoUrl: task.report.photo_url,
          timestamp: new Date(task.report.created_at),
          reporterName: task.report.reporter?.display_name || "Worker",
        } as any : undefined,
        worker_name: task.worker?.display_name || task.worker?.full_name || "Unknown",
      }));

      setEscalatedTasks(tasks);
    } catch (error) {
      console.error("Error fetching escalated tasks:", error);
    }
  };

  // Fetch chat history for a specific report
  const fetchChatHistory = async (reporterId: string) => {
    setLoadingHistory(true);
    setChatHistory([]);
    try {
      // Get the worker's user_id from worker_profiles
      const { data: workerProfile } = await supabase
        .from("worker_profiles")
        .select("user_id")
        .eq("id", reporterId)
        .maybeSingle();

      if (!workerProfile?.user_id) {
        setLoadingHistory(false);
        return;
      }

      // Fetch the most recent conversation history for this user
      const { data: conversations, error } = await supabase
        .from("conversation_history")
        .select("messages, created_at")
        .eq("user_id", workerProfile.user_id)
        .order("created_at", { ascending: false })
        .limit(1);

      if (error) throw error;

      if (conversations && conversations.length > 0) {
        const messages = conversations[0].messages as Array<{ role: string; content: string }>;
        // Filter to show only the last 10 messages for context
        setChatHistory(messages.slice(-10));
      }
    } catch (error) {
      console.error("Error fetching chat history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch chat history when a report is selected
  useEffect(() => {
    if (selectedReport && selectedReport.reporterId) {
      fetchChatHistory(selectedReport.reporterId);
    }
  }, [selectedReport]);

  useEffect(() => {
    let filtered = [...reports];

    if (searchQuery) {
      filtered = filtered.filter(
        (r) => r.description.toLowerCase().includes(searchQuery.toLowerCase()) || r.location.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (riskFilter !== "all") {
      filtered = filtered.filter((r) => r.severity === riskFilter);
    }

    setFilteredReports(filtered);
    setCurrentPage(1);
  }, [searchQuery, riskFilter, reports]);

  const totalPages = Math.ceil(filteredReports.length / reportsPerPage);
  const paginatedReports = filteredReports.slice((currentPage - 1) * reportsPerPage, currentPage * reportsPerPage);

  const hotspots = reports.reduce((acc, report) => {
    acc[report.location] = (acc[report.location] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // JUST CULTURE: Actions - "confirm" = confirm hazard exists, "reject" = reject report
  const handleAction = async (action: "confirm" | "reject", reportId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("process-supervisor-decision", {
        body: {
          reportId,
          decision: action === "confirm" ? "approve" : "rejected",
          supervisorNotes: supervisorNotes || "",
        },
      });

      if (error) throw error;

      toast({
        title: "Berhasil",
        description: action === "confirm" ? "Bahaya dikonfirmasi" : "Laporan ditutup",
      });

      fetchReports();
      setSelectedReport(null);
      setSupervisorNotes("");
    } catch (error) {
      console.error("Error updating report:", error);
      toast({ title: "Error", description: "Gagal memproses keputusan", variant: "destructive" });
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;

      const { data: profile } = await supabase
        .from("worker_profiles")
        .select("id")
        .eq("user_id", authUser.id)
        .maybeSingle();

      await supabase
        .from("hazard_resolutions")
        .update({
          resolved_at: new Date().toISOString(),
          resolved_by: profile?.id,
          action_taken: completionNotes,
          photo_after: afterPhotoUrl || null,
        })
        .eq("id", taskId);

      toast({ title: "Tugas Selesai", description: "Tindakan korektif telah dicatat" });
      setSelectedTask(null);
      setCompletionNotes("");
      setAfterPhotoUrl("");
      fetchEscalatedTasks();
    } catch (error) {
      console.error("Error completing task:", error);
      toast({ title: "Error", description: "Gagal menyelesaikan tugas", variant: "destructive" });
    }
  };

  const handleAfterPhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const fileName = `after-photos/${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from("hazard-evidence")
        .upload(fileName, file);

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from("hazard-evidence")
        .getPublicUrl(fileName);

      setAfterPhotoUrl(urlData.publicUrl);
      toast({ title: "Foto Diunggah", description: "Foto bukti berhasil diunggah" });
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast({ title: "Error", description: "Gagal mengunggah foto", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  if (!user) return null;

  // Detail View for Reports
  if (selectedReport) {
    return (
      <div className="min-h-screen bg-background">
        <GeminiHeader 
          title="Investigation Pack" 
          showBack={false}
          rightElement={
            <Button variant="ghost" size="icon" onClick={() => setSelectedReport(null)} className="h-10 w-10 rounded-full">
              <XCircle className="h-5 w-5" />
            </Button>
          }
        />

        <main className="max-w-2xl mx-auto px-4 py-6">

          <div className="space-y-4">
            {/* Report Quality Score */}
            {selectedReport.evidence_score !== undefined && selectedReport.evidence_score !== null && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card rounded-2xl p-5 border border-border/50">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-semibold text-lg">Skor Kualitas Laporan</span>
                  <span className="text-3xl font-bold">{selectedReport.evidence_score}/100</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden mb-3">
                  <div
                    className={`h-full transition-all ${(selectedReport.evidence_score || 0) >= 55 ? "bg-success" : (selectedReport.evidence_score || 0) >= 25 ? "bg-warning" : "bg-destructive"}`}
                    style={{ width: `${selectedReport.evidence_score || 0}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {(selectedReport.evidence_score || 0) >= 55 && "✓ Kualitas tinggi - auto-confirmed"}
                  {(selectedReport.evidence_score || 0) >= 25 && (selectedReport.evidence_score || 0) < 55 && "~ Kualitas sedang - butuh peer corroboration"}
                  {(selectedReport.evidence_score || 0) < 25 && "✗ Kualitas rendah - pending verification"}
                </p>
                
                <div className="pt-4 border-t border-border/50 mt-4 text-sm text-muted-foreground">
                  <p>Faktor skor: Bukti foto, kualitas deskripsi, GPS, kredibilitas pelapor, analisis penyebab</p>
                </div>
              </motion.div>
            )}

            {/* Report Details - Enhanced */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card rounded-2xl p-5 border border-border/50">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg mb-1">{selectedReport.location}</h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <UserIcon className="h-3 w-3" />
                    <span>By: {selectedReport.reporterName}</span>
                  </div>
                </div>
                <RiskRatingBadge rating={selectedReport.severity as any} score={0} showScore={false} />
              </div>

              <div className="space-y-3">
                {/* Hazard Category */}
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium capitalize">{selectedReport.hazard_category?.replace(/_/g, ' ') || 'Unknown'}</span>
                </div>

                {/* Timestamp */}
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{format(selectedReport.timestamp, 'dd MMM yyyy, HH:mm')}</span>
                </div>

                {/* Reported Worker */}
                {selectedReport.reported_worker_name && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-destructive/10 border border-destructive/20">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    <span className="text-sm">Pekerja dilaporkan: <strong>{selectedReport.reported_worker_name}</strong></span>
                  </div>
                )}

                <div>
                  <div className="text-xs font-semibold text-muted-foreground mb-1">Description</div>
                  <p className="text-sm">{selectedReport.description}</p>
                </div>

                {selectedReport.photoUrl && (
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-2">Photo Evidence</div>
                    <img src={selectedReport.photoUrl} alt="Evidence" className="rounded-xl w-full max-w-md" />
                  </div>
                )}
              </div>
            </motion.div>

            {/* Chat History Context */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="glass-card rounded-2xl p-5 border border-border/50">
              <div className="text-sm font-semibold mb-3 flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                Riwayat Percakapan
              </div>
              {loadingHistory ? (
                <div className="text-sm text-muted-foreground">Loading...</div>
              ) : chatHistory.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {chatHistory.map((msg, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg text-sm ${
                        msg.role === 'user' 
                          ? 'bg-primary/10 ml-4' 
                          : 'bg-muted mr-4'
                      }`}
                    >
                      <div className="text-xs font-medium text-muted-foreground mb-1">
                        {msg.role === 'user' ? 'Pekerja' : 'SORIN AI'}
                      </div>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Tidak ada riwayat percakapan</p>
              )}
            </motion.div>

            {/* Notes */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-card rounded-2xl p-5 border border-border/50">
              <div className="text-sm font-semibold mb-2">Supervisor Notes</div>
              <Textarea value={supervisorNotes} onChange={(e) => setSupervisorNotes(e.target.value)} placeholder="Add notes..." rows={3} className="rounded-xl" />
            </motion.div>

            {/* Actions - JUST CULTURE: Confirm Hazard Exists, Needs More Info, Reject */}
            {/* Show clarification response if available */}
            {selectedReport.clarification_response && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="glass-card rounded-2xl p-5 border border-success/30 bg-success/5">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="h-5 w-5 text-success" />
                  <span className="font-semibold">Klarifikasi dari Pelapor</span>
                  {selectedReport.clarification_responded_at && (
                    <span className="text-xs text-muted-foreground ml-auto">
                      {format(new Date(selectedReport.clarification_responded_at), 'dd MMM yyyy, HH:mm')}
                    </span>
                  )}
                </div>
                <p className="text-sm">{selectedReport.clarification_response}</p>
              </motion.div>
            )}

            {/* Actions - JUST CULTURE: Confirm Hazard Exists or Reject */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="grid grid-cols-2 gap-3">
              <Button variant="outline" onClick={() => handleAction("reject", selectedReport.id)} className="flex-col h-auto py-4 rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10">
                <XCircle className="h-5 w-5 mb-1" />
                <span className="text-xs">Tolak</span>
              </Button>
              <Button onClick={() => handleAction("confirm", selectedReport.id)} className="flex-col h-auto py-4 rounded-xl bg-success hover:bg-success/90">
                <CheckCircle className="h-5 w-5 mb-1" />
                <span className="text-xs">Konfirmasi</span>
              </Button>
            </motion.div>
          </div>
        </main>
      </div>
    );
  }

  // Detail View for Escalated Task
  if (selectedTask) {
    return (
      <div className="min-h-screen bg-background">
        <GeminiHeader 
          title="Corrective Action" 
          showBack={false}
          rightElement={
            <Button variant="ghost" size="icon" onClick={() => setSelectedTask(null)} className="h-10 w-10 rounded-full">
              <XCircle className="h-5 w-5" />
            </Button>
          }
        />

        <main className="max-w-2xl mx-auto px-4 py-6">
          <div className="space-y-4">
            {/* Task Info */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card rounded-2xl p-5 border border-border/50">
              <div className="flex items-center gap-2 mb-3">
                <Wrench className="h-5 w-5 text-warning" />
                <span className="font-semibold">Eskalasi ke {selectedTask.escalated_to}</span>
              </div>
              
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="text-xs text-muted-foreground mb-1">Lokasi</div>
                  <div className="font-medium">{selectedTask.report?.location || 'N/A'}</div>
                </div>
                
                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="text-xs text-muted-foreground mb-1">Hazard</div>
                  <div className="text-sm">{selectedTask.report?.description || 'N/A'}</div>
                </div>

                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="text-xs text-muted-foreground mb-1">Tindakan yang Disarankan</div>
                  <div className="text-sm">{selectedTask.action_suggested || 'Tidak ada saran spesifik'}</div>
                </div>

                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="text-xs text-muted-foreground mb-1">Diajukan oleh</div>
                  <div className="text-sm">{selectedTask.worker_name}</div>
                </div>

                {selectedTask.escalated_at && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>Dieskalasi: {format(new Date(selectedTask.escalated_at), 'dd MMM yyyy, HH:mm')}</span>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Completion Form */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card rounded-2xl p-5 border border-border/50">
              <div className="text-sm font-semibold mb-3">Selesaikan Tugas</div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground">Tindakan yang Diambil</label>
                  <Textarea 
                    value={completionNotes} 
                    onChange={(e) => setCompletionNotes(e.target.value)} 
                    placeholder="Jelaskan tindakan korektif yang sudah dilakukan..." 
                    rows={3} 
                    className="mt-1 rounded-xl" 
                  />
                </div>

                <div>
                  <label className="text-sm text-muted-foreground">Foto Bukti (Sesudah)</label>
                  <div className="mt-2">
                    {afterPhotoUrl ? (
                      <div className="relative">
                        <img src={afterPhotoUrl} alt="After" className="rounded-xl w-full max-h-48 object-cover" />
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          className="absolute top-2 right-2"
                          onClick={() => setAfterPhotoUrl("")}
                        >
                          Hapus
                        </Button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-border rounded-xl cursor-pointer hover:bg-muted/50 transition-colors">
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={handleAfterPhotoUpload}
                          disabled={isUploading}
                          style={{
                            position: 'absolute',
                            width: '1px',
                            height: '1px',
                            padding: 0,
                            margin: '-1px',
                            overflow: 'hidden',
                            clip: 'rect(0, 0, 0, 0)',
                            opacity: 0,
                            pointerEvents: 'none',
                          }}
                        />
                        {isUploading ? (
                          <span className="text-sm text-muted-foreground">Mengunggah...</span>
                        ) : (
                          <>
                            <Camera className="h-8 w-8 text-muted-foreground mb-2" />
                            <span className="text-sm text-muted-foreground">Tap untuk ambil foto</span>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="grid grid-cols-2 gap-3">
              <Button variant="outline" onClick={() => setSelectedTask(null)} className="rounded-xl">
                Batal
              </Button>
              <Button 
                onClick={() => handleCompleteTask(selectedTask.id)} 
                className="rounded-xl bg-success hover:bg-success/90"
                disabled={!completionNotes.trim()}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Selesai
              </Button>
            </motion.div>
          </div>
        </main>
      </div>
    );
  }

  // List View with Tabs
  return (
    <div className="min-h-screen bg-background">
      <GeminiHeader 
        title="Supervisor Dashboard" 
        subtitle="Validasi laporan bahaya"
      />

      <main className="max-w-4xl mx-auto px-4 py-6">

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <SupervisorTabs 
            activeTab={activeTab} 
            setActiveTab={setActiveTab}
            filteredReportsCount={filteredReports.length}
            escalatedTasksCount={escalatedTasks.length}
          />

          <TabsContent value="reports" className="mt-6 space-y-6">
            {/* Hotspots */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card rounded-2xl p-5 border border-border/50">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-destructive" />
                Hazard Hotspots
              </h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(hotspots)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 5)
                  .map(([location, count]) => (
                    <span key={location} className="px-3 py-1.5 rounded-full bg-destructive/10 text-destructive text-sm font-medium">
                      {location} ({count})
                    </span>
                  ))}
                {Object.keys(hotspots).length === 0 && (
                  <span className="text-sm text-muted-foreground">No hotspots identified</span>
                )}
              </div>
            </motion.div>

            {/* Filters */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-card rounded-2xl p-5 border border-border/50">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search reports..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 h-11 rounded-xl" />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full md:w-48 h-11 rounded-xl">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Status</SelectItem>
                    <SelectItem value="pending_verification">Pending Verification</SelectItem>
                    <SelectItem value="peer_corroboration_requested">Menunggu Verifikasi Peer</SelectItem>
                    <SelectItem value="hazard_confirmed">Dikonfirmasi</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={riskFilter} onValueChange={setRiskFilter}>
                  <SelectTrigger className="w-full md:w-48 h-11 rounded-xl">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Risk Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Risk</SelectItem>
                    <SelectItem value="very_high">Very High</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </motion.div>

            {/* Reports */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="space-y-3">
              {isLoading ? (
                <div className="glass-card rounded-2xl p-12 text-center text-muted-foreground border border-border/50">Loading...</div>
              ) : paginatedReports.length === 0 ? (
                <div className="glass-card rounded-2xl p-12 text-center text-muted-foreground border border-border/50">No reports found</div>
              ) : (
                paginatedReports.map((report) => (
                  <motion.div
                    key={report.id}
                    whileHover={{ scale: 1.01 }}
                    className="glass-card rounded-2xl p-4 border border-border/50 cursor-pointer hover:border-primary/30 transition-colors"
                    onClick={() => setSelectedReport(report)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{report.location}</h3>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-muted capitalize">{report.hazard_category?.replace(/_/g, ' ') || 'Unknown'}</span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{report.description}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <UserIcon className="h-3 w-3" />
                            {report.reporterName}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(report.timestamp, 'dd MMM, HH:mm')}
                          </span>
                          {report.truth_score !== undefined && (
                            <span className={`font-medium ${(report.truth_score || 0) >= 0.8 ? "text-success" : (report.truth_score || 0) >= 0.6 ? "text-warning" : "text-destructive"}`}>
                              Trust: {Math.round((report.truth_score || 0) * 100)}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <RiskRatingBadge rating={report.severity as any} score={0} showScore={false} />
                        {report.photoUrl && <Camera className="h-4 w-4 text-muted-foreground" />}
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </motion.div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <Button variant="outline" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="rounded-xl">
                  Previous
                </Button>
                <span className="flex items-center px-4 text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button variant="outline" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="rounded-xl">
                  Next
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="tasks" className="mt-6 space-y-4">
            {escalatedTasks.length === 0 ? (
              <div className="glass-card rounded-2xl p-12 text-center text-muted-foreground border border-border/50">
                <Wrench className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Tidak ada tugas koreksi yang menunggu</p>
              </div>
            ) : (
              escalatedTasks.map((task) => (
                <motion.div
                  key={task.id}
                  whileHover={{ scale: 1.01 }}
                  className="glass-card rounded-2xl p-4 border border-warning/30 cursor-pointer hover:border-warning/50 transition-colors"
                  onClick={() => setSelectedTask(task)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Wrench className="h-4 w-4 text-warning" />
                        <h3 className="font-semibold">{task.report?.location || 'Unknown Location'}</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-warning/20 text-warning capitalize">
                          {task.escalated_to}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                        {task.action_suggested || task.report?.description || 'No description'}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <UserIcon className="h-3 w-3" />
                          Dari: {task.worker_name}
                        </span>
                        {task.escalated_at && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(new Date(task.escalated_at), 'dd MMM, HH:mm')}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="rounded-lg">
                      Selesaikan
                    </Button>
                  </div>
                </motion.div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Supervisor;
